import { TARGET_LEAVING_MAX_MINUTES, TARGET_MAX_COUNT, TARGET_MAX_EXCLUDE, TARGET_MAX_IDS, TARGET_OKAY_MAX_HOURS, type Observation, type TargetCandidateRow, type TargetQuery } from './player-db';

/**
 * Finding targets — one copy for the extension (core/targets.ts re-exports the fair-fight maths below) and the website's
 * Target finder page (website roadmap S03, FF Scouter's target finder: apps/web/src/app/pages/targets.ts).
 *
 *  - Fair fight: FF = 1 + 8/3 × their battle score ÷ yours, capped at 3. A battle score is the sum of the square roots of
 *    the four stats; a total of T is about a score of 2 × √(T ÷ 1.04) (scoreFromTotal).
 *  - The server is asked (POST /api/targets/pick) with a rough battle-stat BAND only: your total snapped to fixed edges
 *    10^(k/2) (steps of ×√10 ≈ 3.16) — never your stats. The exact fair fight is worked out here, in your browser.
 *  - Presets as FF Scouter's (ffscouter.com/guides/target-finder): Levelling (inactive, FF up to 3, highest level first),
 *    Respect / chain (inactive, FF 2–3, strongest first), Custom (levels, FF band, inactive only, no faction only, 1–50),
 *    and your saved players (lists sync: the extension's saved targets, `SAVED_TARGETS_LIST`).
 *  - Live checks use the viewer's own key (Torn's profile: status, life, last action), on their click only.
 *  - Event presets (website roadmap S16, FF Scouter's): "Not in hospital" (players seen out of hospital lately, plus a
 *    hospital-exit window: those leaving within N minutes, soonest first) and "Elimination" (one team's members — read
 *    with your own key — from the player database). The server answers from the database's public facts only.
 */

// ---------------------------------------------------------------- fair fight

/** The fair fight you'd get from your and their battle stat scores (capped at 3 like Torn's). */
export function fairFightFor(yourScore: number | null, theirScore: number | null): number | null {
  if (!yourScore || !theirScore || yourScore <= 0) return null;
  return Math.min(3, 1 + (8 / 3) * (theirScore / yourScore));
}

/** Fair fight without the cap: how strong they are next to you. */
export function rawFairFight(yourScore: number | null, theirScore: number | null): number | null {
  if (!yourScore || !theirScore || yourScore <= 0) return null;
  return 1 + (8 / 3) * (theirScore / yourScore);
}

/** Their score from an estimated total (total ≈ 1.04 × score² / 4). */
export function scoreFromTotal(total: number): number {
  return 2 * Math.sqrt(total / 1.04);
}

/** Their estimated total from a score (the inverse of scoreFromTotal). */
export function totalFromScore(score: number): number {
  return (1.04 * score * score) / 4;
}

/** Your battle score from your four stats (the sum of their square roots); null when they aren't all known. */
export function scoreOfStats(s: { strength?: number | null; defense?: number | null; speed?: number | null; dexterity?: number | null } | null | undefined): number | null {
  if (!s) return null;
  const v = [s.strength, s.defense, s.speed, s.dexterity];
  if (!v.every((x) => typeof x === 'number' && Number.isFinite(x) && x >= 0)) return null;
  const score = (v as number[]).reduce((a, x) => a + Math.sqrt(x), 0);
  return score > 0 ? score : null;
}

/**
 * The bands a battle-stat range is sent in: fixed edges 10^(k/2) — 1, 3.16, 10, 31.6, 100 … — each about ×3.16 the last.
 * A range is snapped OUTWARD to these edges, so the server learns only a wide band, never your stats.
 */
export const BAND_STEP = Math.sqrt(10);

/** The band edge at or below `n` (down) or at or above it (up); 0 for nothing. Whole numbers. */
export function bandEdge(n: number, up: boolean): number {
  if (!(n > 0) || !Number.isFinite(n)) return 0;
  const half = 2 * Math.log10(n);
  const k = up ? Math.ceil(half - 1e-9) : Math.floor(half + 1e-9);
  const edge = Math.pow(10, k / 2);
  return up ? Math.ceil(edge - 1e-6) : Math.floor(edge + 1e-6);
}

/**
 * The battle stat total range that gives a fair fight between `minFf` and `maxFf`, worked out from YOUR BAND, not your
 * stats: your own total is first snapped to its fixed band (bandEdge), the low end comes from the band's lower edge and
 * the high end from its upper edge, widened, then snapped outward. So every total of yours inside one band (×3.16) gives
 * the very same range — only a rough battle-stat band (steps of about ×3) worked out from yours is ever sent.
 */
export function totalRangeForFf(yourScore: number | null, minFf: number, maxFf: number): { totalMin: number; totalMax: number } | null {
  if (!yourScore || !(yourScore > 0)) return null;
  const k = Math.floor(2 * Math.log10(Math.max(1, totalFromScore(yourScore))) + 1e-9);
  const bandLo = scoreFromTotal(Math.pow(10, k / 2));
  const bandHi = scoreFromTotal(Math.pow(10, (k + 1) / 2));
  const score = (ff: number, yours: number) => (3 / 8) * (ff - 1) * yours;
  const lo = minFf <= 1 ? 0 : totalFromScore(score(minFf, bandLo)) * 0.8;
  // A fair fight of 3 is the cap: "at least that strong" — a little above it is still worth a look.
  const hi = totalFromScore(score(maxFf, bandHi)) * 1.25;
  return { totalMin: bandEdge(lo, false), totalMax: bandEdge(hi, true) };
}

// ---------------------------------------------------------------- the website's target finder

export type TargetPreset = 'levelling' | 'respect' | 'custom' | 'outnow' | 'elimination' | 'saved';

export interface TargetFilters {
  levelMin: number;
  levelMax: number;
  /** The fair fight band (1–3; Torn caps it at 3). */
  ffMin: number;
  ffMax: number;
  /** Only players whose last action is at least INACTIVE_DAYS ago. */
  inactive: boolean;
  /** Only players in no faction. */
  factionless: boolean;
  /** How many to list (1–50). */
  count: number;
}

/** "Inactive" = last action at least this many days ago. */
export const INACTIVE_DAYS = 7;

export const PRESET_NAMES: Record<TargetPreset, string> = { levelling: 'Levelling', respect: 'Respect / chain', custom: 'Custom', outnow: 'Not in hospital', elimination: 'Elimination', saved: 'Saved' };

/** The event presets (S16) search with your Custom levels and fair fight band, and these. */
export interface EventOptions {
  /** Only players seen out of hospital within this many hours (1–TARGET_OKAY_MAX_HOURS). */
  okayHours: number;
  /** Also those leaving hospital within this many minutes (0 = no; up to TARGET_LEAVING_MAX_MINUTES). */
  leavingMinutes: number;
}

export const DEFAULT_EVENT: EventOptions = { okayHours: 6, leavingMinutes: 15 };

export function cleanEventOptions(raw: unknown, fallback: EventOptions = DEFAULT_EVENT): EventOptions {
  const o = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  return { okayHours: numIn(o.okayHours, 1, TARGET_OKAY_MAX_HOURS, fallback.okayHours, true), leavingMinutes: numIn(o.leavingMinutes, 0, TARGET_LEAVING_MAX_MINUTES, fallback.leavingMinutes, true) };
}

export const EVENT_BLURBS: Record<'outnow' | 'elimination', string> = {
  outnow: 'Players seen out of hospital lately — for events where every hit counts (Trick or Treat, chains) — with your Custom levels, fair fight and faction choice. Add those leaving hospital soon: they come last, soonest out first.',
  elimination: 'Elimination: pick a team, and its members come from Omni-Torn’s player database with your fair fight — only those seen out of hospital lately (and, if you like, leaving soon).',
};

/** The two fixed presets (FF Scouter's): only the count changes. */
export const TARGET_PRESETS: Record<'levelling' | 'respect', { blurb: string; filters: Omit<TargetFilters, 'count'>; sort: 'level' | 'estimate' }> = {
  levelling: {
    blurb: `Inactive players (${INACTIVE_DAYS}+ days) you should beat — fair fight up to 3 — highest level first: for experience.`,
    filters: { levelMin: 1, levelMax: 100, ffMin: 1, ffMax: 3, inactive: true, factionless: false },
    sort: 'level',
  },
  respect: {
    blurb: `Inactive players (${INACTIVE_DAYS}+ days) with a fair fight of 2 to 3, strongest first: the most respect a hit, for chains.`,
    filters: { levelMin: 1, levelMax: 100, ffMin: 2, ffMax: 3, inactive: true, factionless: false },
    sort: 'estimate',
  },
};

export const DEFAULT_CUSTOM: TargetFilters = { levelMin: 1, levelMax: 100, ffMin: 1.5, ffMax: 2.5, inactive: false, factionless: false, count: 20 };

const numIn = (x: unknown, lo: number, hi: number, dflt: number, whole: boolean): number => {
  const n = typeof x === 'string' && x.trim() !== '' ? Number(x) : x;
  if (typeof n !== 'number' || !Number.isFinite(n)) return dflt;
  const v = Math.min(hi, Math.max(lo, n));
  return whole ? Math.round(v) : Math.round(v * 100) / 100;
};

/** Filters as typed (or kept): numbers clamped to their ranges, low and high the right way round. */
export function cleanFilters(raw: unknown, fallback: TargetFilters = DEFAULT_CUSTOM): TargetFilters {
  const o = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  let levelMin = numIn(o.levelMin, 1, 100, fallback.levelMin, true);
  let levelMax = numIn(o.levelMax, 1, 100, fallback.levelMax, true);
  if (levelMin > levelMax) [levelMin, levelMax] = [levelMax, levelMin];
  let ffMin = numIn(o.ffMin, 1, 3, fallback.ffMin, false);
  let ffMax = numIn(o.ffMax, 1, 3, fallback.ffMax, false);
  if (ffMin > ffMax) [ffMin, ffMax] = [ffMax, ffMin];
  return {
    levelMin,
    levelMax,
    ffMin,
    ffMax,
    inactive: typeof o.inactive === 'boolean' ? o.inactive : fallback.inactive,
    factionless: typeof o.factionless === 'boolean' ? o.factionless : fallback.factionless,
    count: numIn(o.count, 1, TARGET_MAX_COUNT, fallback.count, true),
  };
}

/** The filters a preset searches with (custom and the event presets: as typed — Elimination ignores "no faction" and "inactive"; the others: theirs, with your count). */
export function presetFilters(preset: Exclude<TargetPreset, 'saved'>, custom: TargetFilters): TargetFilters {
  if (preset === 'elimination') return { ...cleanFilters(custom), inactive: false, factionless: false };
  if (preset === 'outnow') return { ...cleanFilters(custom), inactive: false };
  return preset === 'custom' ? cleanFilters(custom) : { ...TARGET_PRESETS[preset].filters, count: cleanFilters(custom).count };
}

/**
 * The question for POST /api/targets/pick: levels, faction/inactive, and only a rough battle-stat band from yours (none
 * without your stats). Always the most the server gives (50): the exact fair fight is worked out here and leaves some
 * out; players the database has no estimate for yet come too (they're worth a check, listed after the known ones).
 */
export function finderQuery(f: TargetFilters, yourScore: number | null, exclude: Iterable<number> = [], event?: { options: EventOptions; ids?: readonly number[] }): TargetQuery {
  const q: TargetQuery = { count: TARGET_MAX_COUNT };
  if (event) {
    const o = cleanEventOptions(event.options);
    q.okayWithinHours = o.okayHours;
    if (o.leavingMinutes > 0) q.leavingWithinMinutes = o.leavingMinutes;
    const ids = [...new Set((event.ids ?? []).filter((id) => Number.isInteger(id) && id > 0))].slice(0, TARGET_MAX_IDS);
    if (ids.length) q.ids = ids;
  }
  if (f.levelMin > 1) q.levelMin = f.levelMin;
  if (f.levelMax < 100) q.levelMax = f.levelMax;
  const total = totalRangeForFf(yourScore, f.ffMin, f.ffMax);
  if (total) Object.assign(q, total, { includeUnknownTotal: true });
  if (f.factionless) q.faction = 'none';
  if (f.inactive) q.inactiveDaysMin = INACTIVE_DAYS;
  const ids = [...new Set([...exclude].filter((id) => Number.isInteger(id) && id > 0))].slice(0, TARGET_MAX_EXCLUDE);
  if (ids.length) q.exclude = ids;
  return q;
}

/** One line of the website's target list. Times are ms. */
export interface FinderRow {
  id: number;
  name: string | null;
  level: number | null;
  factionId: number | null;
  /** Estimated battle stat total, and how it was worked out ('fight' | 'pool' | 'rank' from Omni-Torn, or 'FF Scouter'). */
  estimate: number | null;
  estimateFrom: string | null;
  /** Fair fight you'd get (capped at 3), and uncapped (how much stronger than the cap). */
  ff: number | null;
  rawFf: number | null;
  lastAction: number | null;
  /** Torn's status as last seen (by anyone for Omni-Torn's rows; by your check once checked). */
  status: string | null;
  statusUntil: number | null;
  description: string | null;
  life: { current: number; maximum: number } | null;
  /** When that status was seen (null: never). */
  seenAt: number | null;
  /** The status came from your own team list (Elimination), not from an Omni-Torn user's sighting. */
  fromTeam?: boolean;
  /** Checked by you on this page (then status/life/last action are yours, fresh). */
  checked: boolean;
  source: 'omni' | 'ffscouter' | 'saved';
}

const ms = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) && x > 0 ? (x < 1e11 ? x * 1000 : x) : null);
const int = (x: unknown): number | null => (typeof x === 'number' && Number.isInteger(x) && x > 0 ? x : null);

/** One of the server's candidates as a row (its fair fight from its estimate and your score, here). */
export function rowFromPick(r: TargetCandidateRow, yourScore: number | null, source: FinderRow['source'] = 'omni'): FinderRow | null {
  if (!r || !int(r.id)) return null;
  const total = typeof r.total === 'number' && r.total > 0 ? r.total : null;
  const theirs = total ? scoreFromTotal(total) : null;
  return {
    id: r.id,
    name: typeof r.name === 'string' ? r.name : null,
    level: int(r.level),
    factionId: typeof r.factionId === 'number' ? r.factionId : null,
    estimate: total,
    estimateFrom: total ? (r.totalSource ?? null) : null,
    ff: fairFightFor(yourScore, theirs),
    rawFf: rawFairFight(yourScore, theirs),
    lastAction: ms(r.lastAction),
    status: typeof r.status === 'string' ? r.status : null,
    statusUntil: ms(r.statusUntil),
    description: null,
    life: null,
    seenAt: ms(r.checkedAt),
    checked: false,
    source,
  };
}

/** FF Scouter's target list as the extension hands it to the website (its own answer, for you only — never stored). */
export interface FfsTarget {
  id: number;
  name: string | null;
  level: number | null;
  /** FF Scouter's fair fight against you. */
  ff: number | null;
  estimate: number | null;
  /** Unix seconds or ms. */
  lastAction: number | null;
}

/** What the website asks the extension for (its FF Scouter connection): the filters, as FF Scouter takes them. */
export interface FfsFilters {
  minLevel: number;
  maxLevel: number;
  minFf: number;
  maxFf: number;
  inactive: boolean;
  factionless: boolean;
  limit: number;
}

export function ffsFiltersFor(f: TargetFilters): FfsFilters {
  return { minLevel: f.levelMin, maxLevel: f.levelMax, minFf: f.ffMin, maxFf: f.ffMax, inactive: f.inactive, factionless: f.factionless, limit: f.count };
}

/** A website's ask, checked (null: refused). */
export function cleanFfsFilters(raw: unknown): FfsFilters | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const n = (x: unknown, lo: number, hi: number) => (typeof x === 'number' && Number.isFinite(x) && x >= lo && x <= hi ? x : null);
  const minLevel = n(o.minLevel, 1, 100);
  const maxLevel = n(o.maxLevel, 1, 100);
  const minFf = n(o.minFf, 1, 3);
  const maxFf = n(o.maxFf, 1, 3);
  const limit = n(o.limit, 1, TARGET_MAX_COUNT);
  if (minLevel === null || maxLevel === null || minFf === null || maxFf === null || limit === null || typeof o.inactive !== 'boolean' || typeof o.factionless !== 'boolean') return null;
  if (minLevel > maxLevel || minFf > maxFf) return null;
  return { minLevel: Math.round(minLevel), maxLevel: Math.round(maxLevel), minFf: Math.round(minFf * 100) / 100, maxFf: Math.round(maxFf * 100) / 100, inactive: o.inactive, factionless: o.factionless, limit: Math.round(limit) };
}

export function rowFromFfs(t: FfsTarget, yourScore: number | null): FinderRow | null {
  if (!t || !int(t.id)) return null;
  const est = typeof t.estimate === 'number' && t.estimate > 0 ? t.estimate : null;
  const theirs = est ? scoreFromTotal(est) : null;
  const ff = typeof t.ff === 'number' && t.ff >= 1 ? Math.min(3, t.ff) : fairFightFor(yourScore, theirs);
  return {
    id: t.id,
    name: typeof t.name === 'string' ? t.name : null,
    level: int(t.level),
    factionId: null,
    estimate: est,
    estimateFrom: est ? 'FF Scouter' : null,
    ff,
    rawFf: rawFairFight(yourScore, theirs) ?? (ff !== null && ff < 2.95 ? ff : null),
    lastAction: ms(t.lastAction),
    status: null,
    statusUntil: null,
    description: null,
    life: null,
    seenAt: null,
    checked: false,
    source: 'ffscouter',
  };
}

/** How strong they are for the band: a fair fight under the cap is exact; at the cap, the uncapped figure. */
function strength(r: FinderRow): number | null {
  return r.ff !== null && r.ff < 2.95 ? r.ff : (r.rawFf ?? null);
}

export interface ShapedList {
  rows: FinderRow[];
  /** Listed with no estimate anywhere yet (after every known one). */
  unknown: number;
  /** Left out, by why. */
  left: { faction: number; band: number; level: number; active: number };
}

/**
 * The list to show: no faction mates, no repeats, within the levels, inactive/no-faction as asked, within the fair fight
 * band (a capped 3 with nothing known beyond it is left out — it only says "at least that strong"); players nobody has
 * an estimate for yet after every known one. Sorted as the preset says, `count` at most.
 */
export function shapeList(rows: readonly FinderRow[], f: TargetFilters, sort: 'level' | 'estimate' | 'ff' | 'exit', opts: { myFactionId?: number | null; me?: number | null; now: number }): ShapedList {
  const left = { faction: 0, band: 0, level: 0, active: 0 };
  const seen = new Set<number>();
  const known: FinderRow[] = [];
  const unknown: FinderRow[] = [];
  for (const r of rows) {
    if (seen.has(r.id) || r.id === opts.me) continue;
    seen.add(r.id);
    if (opts.myFactionId && r.factionId === opts.myFactionId) {
      left.faction++;
      continue;
    }
    if (r.level !== null && (r.level < f.levelMin || r.level > f.levelMax)) {
      left.level++;
      continue;
    }
    if ((f.inactive && r.lastAction !== null && opts.now - r.lastAction < INACTIVE_DAYS * 86_400_000) || (f.factionless && r.factionId !== null && r.factionId > 0)) {
      left.active++;
      continue;
    }
    const s = strength(r);
    if (s === null && r.ff !== null && r.ff >= 2.95) {
      // "3" alone: at least that strong — only when 3 is all you asked for is that enough.
      if (f.ffMax < 3) {
        left.band++;
        continue;
      }
    } else if (s !== null && (s > f.ffMax + 0.05 || (r.ff ?? s) < f.ffMin - 0.05)) {
      left.band++;
      continue;
    }
    (r.ff === null && r.rawFf === null && r.estimate === null ? unknown : known).push(r);
  }
  const by = (r: FinderRow) => (sort === 'level' ? (r.level ?? 0) : sort === 'estimate' ? (r.estimate ?? 0) : (r.ff ?? 0));
  // 'exit' (the event presets): out of hospital now first (best fair fight first), then by when they leave, soonest first.
  const exit = (r: FinderRow) => (sort === 'exit' ? leavesAt(r, opts.now) : 0);
  known.sort((a, b) => exit(a) - exit(b) || by(b) - by(a) || (b.level ?? 0) - (a.level ?? 0) || a.id - b.id);
  unknown.sort((a, b) => (b.level ?? 0) - (a.level ?? 0) || a.id - b.id);
  // 'exit': everyone out now (known, then not known yet) before anyone still in hospital, soonest out first.
  const out = (sort === 'exit' ? [...known, ...unknown].sort((a, b) => exit(a) - exit(b)) : [...known, ...unknown]).slice(0, f.count);
  return { rows: out, unknown: out.filter((r) => unknown.includes(r)).length, left };
}

/** When they leave hospital (0: not in hospital, or already out). */
export function leavesAt(r: FinderRow, now: number): number {
  return /^hospital$/i.test(r.status ?? '') && r.statusUntil !== null && r.statusUntil > now ? r.statusUntil : 0;
}

// ---------------------------------------------------------------- Elimination (S16): teams, read with your own key

export interface EliminationTeam {
  id: number;
  name: string;
  score: number | null;
  lives: number | null;
  participants: number | null;
  position: number | null;
  eliminated: boolean;
}

export interface EliminationMember {
  id: number;
  name: string | null;
  level: number | null;
  state: string | null;
  until: number | null;
  lastAction: number | null;
}

const listIn = (b: Obj | null, keys: readonly string[]): unknown[] => {
  for (const k of keys) {
    const v = b?.[k];
    if (Array.isArray(v)) return v;
    if (v && typeof v === 'object') return Object.entries(v as Obj).map(([id, x]) => (x && typeof x === 'object' && !Array.isArray(x) ? { id, ...(x as Obj) } : x));
  }
  return [];
};

/** Torn's v2 `torn/elimination` (the teams), read leniently; best placed first. */
export function readEliminationTeams(body: unknown): EliminationTeam[] {
  const out: EliminationTeam[] = [];
  for (const x of listIn(obj(body), ['elimination', 'teams'])) {
    const t = obj(x);
    const id = int(typeof t?.id === 'string' ? Number(t.id) : t?.id);
    const name = typeof t?.name === 'string' ? t.name.trim() : '';
    if (!id || !name) continue;
    out.push({ id, name, score: num(t!.score), lives: num(t!.lives), participants: num(t!.participants ?? t!.members), position: num(t!.position), eliminated: t!.eliminated === true || (num(t!.eliminated_timestamp) ?? 0) > 0 });
  }
  return out.sort((a, b) => (a.position ?? 1e9) - (b.position ?? 1e9) || (b.score ?? 0) - (a.score ?? 0) || a.id - b.id);
}

/** One page of Torn's v2 `torn/{teamId}/eliminationteam` (its members), read leniently; `next`: is there another page. */
export function readEliminationMembers(body: unknown): { members: EliminationMember[]; next: boolean } {
  const b = obj(body);
  const members: EliminationMember[] = [];
  for (const x of listIn(b, ['eliminationteam', 'members', 'players'])) {
    const m = obj(x);
    const id = int(typeof m?.id === 'string' ? Number(m.id) : (m?.id ?? m?.user_id));
    if (!id) continue;
    const st = obj(m!.status);
    const la = obj(m!.last_action);
    members.push({ id, name: typeof m!.name === 'string' ? m!.name : null, level: int(m!.level), state: typeof st?.state === 'string' ? st.state : null, until: ms(st?.until), lastAction: ms(la?.timestamp) });
  }
  const links = obj(obj(b?._metadata)?.links);
  return { members, next: typeof links?.next === 'string' && links.next.length > 0 };
}

/** Your own Elimination team's name, from your profile (v1 `competition.team`), if Torn says. */
export function ownEliminationTeam(profile: unknown): string | null {
  const p = obj(profile);
  const c = obj(p?.competition) ?? obj(obj(p?.profile)?.competition);
  const team = c?.team ?? c?.team_name;
  return typeof team === 'string' && team.trim() ? team.trim() : null;
}

/** A target row with what the team list (Torn's answer to your key, just now) says of the member — fresher than the database. */
export function withMember(r: FinderRow, m: EliminationMember | undefined, at: number): FinderRow {
  if (!m) return r;
  return { ...r, name: m.name ?? r.name, level: m.level ?? r.level, lastAction: m.lastAction ?? r.lastAction, ...(m.state ? { status: m.state, statusUntil: m.until, seenAt: at, fromTeam: true } : {}) };
}

// ---------------------------------------------------------------- live checks (the viewer's own key)

/** At most this many live checks a minute from the website (its whole share of the viewer's Torn limit). */
export const TARGET_CHECKS_PER_MINUTE = 20;

/** What one profile check (v1 `/user/<id>?selections=profile`, or v2 `/user/<id>/profile`) says. */
export interface ProfileCheck {
  at: number;
  name: string | null;
  level: number | null;
  factionId: number | null;
  state: string | null;
  description: string | null;
  until: number | null;
  lastAction: number | null;
  lastStatus: string | null;
  life: { current: number; maximum: number } | null;
}

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);

export function readProfileCheck(body: unknown, now: number): ProfileCheck | null {
  const b = obj(body);
  if (!b || b.error) return null;
  // v2 nests it under `profile`; v1 is flat (a `profile` there without a status is just a summary).
  const inner = obj(b.profile);
  const p = inner && obj(inner.status) ? inner : b;
  const st = obj(p.status);
  if (!st && typeof p.name !== 'string') return null;
  const la = obj(p.last_action);
  const life = obj(p.life);
  const fac = obj(p.faction);
  const fid = num(fac?.faction_id ?? p.faction_id);
  return {
    at: now,
    name: typeof p.name === 'string' ? p.name : null,
    level: int(p.level),
    factionId: fid === null ? null : fid,
    state: typeof st?.state === 'string' ? st.state : null,
    description: typeof st?.description === 'string' ? st.description : null,
    until: ms(st?.until),
    lastAction: ms(la?.timestamp),
    lastStatus: typeof la?.status === 'string' ? la.status : null,
    life: life && num(life.current) !== null ? { current: num(life.current)!, maximum: num(life.maximum) ?? 0 } : null,
  };
}

/** The row after your check. */
export function applyCheck(r: FinderRow, c: ProfileCheck): FinderRow {
  return {
    ...r,
    name: c.name ?? r.name,
    level: c.level ?? r.level,
    factionId: c.factionId ?? r.factionId,
    lastAction: c.lastAction ?? r.lastAction,
    status: c.state ?? r.status,
    statusUntil: c.until,
    description: c.description,
    life: c.life,
    seenAt: c.at,
    checked: true,
  };
}

/** What your check saw, for Omni-Torn's player database (terms 6/7: "target checks you ask for") — no extra call. */
export function checkToObservations(id: number, c: ProfileCheck): Observation[] {
  const out: Observation[] = [];
  const profile: Record<string, unknown> = {};
  if (c.name) profile.name = c.name;
  if (c.level !== null) profile.level = c.level;
  if (Object.keys(profile).length) out.push({ id, kind: 'profile', at: c.at, source: 'api', data: profile });
  if (c.state) {
    const data: Record<string, unknown> = { state: c.state, description: c.description, until: c.until ? Math.floor(c.until / 1000) : null };
    if (c.life) Object.assign(data, { life: c.life.current, maxLife: c.life.maximum });
    out.push({ id, kind: 'status', at: c.at, source: 'api', data });
  }
  if (c.lastAction !== null || c.lastStatus) out.push({ id, kind: 'activity', at: c.at, source: 'api', data: { lastAction: c.lastAction ? Math.floor(c.lastAction / 1000) : null, state: c.lastStatus } });
  return out;
}

/** Can you attack them now (as far as known)? true: Okay; false: hospital, jail, flying, abroad, fallen; null: not known. */
export function hittable(r: FinderRow, now: number): boolean | null {
  const st = (r.status ?? '').toLowerCase();
  if (!st) return null;
  if (st === 'okay') return r.checked || (r.seenAt !== null && now - r.seenAt < 10 * 60_000) ? true : null;
  if ((st === 'hospital' || st === 'jail') && r.statusUntil !== null && r.statusUntil <= now) return null;
  return false;
}

// ---------------------------------------------------------------- your saved targets (lists sync)

/** The lists-sync entry that holds the extension's saved targets (`{ saved: [{ id, name, at }] }`, core/list-sync.ts). */
export const SAVED_TARGETS_LIST = 'omnitorn.targets';
/** The extension keeps at most this many. */
export const SAVED_TARGETS_MAX = 500;

export interface SavedTarget {
  id: number;
  name: string | null;
  at: number;
}

/** The saved targets in a lists document (any junk left out). */
export function savedTargetsOf(lists: unknown): SavedTarget[] {
  const entry = obj(obj(lists)?.[SAVED_TARGETS_LIST]);
  const list = Array.isArray(entry?.saved) ? entry!.saved : [];
  const out: SavedTarget[] = [];
  const seen = new Set<number>();
  for (const x of list) {
    const o = obj(x);
    const id = int(o?.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: typeof o!.name === 'string' ? o!.name : null, at: num(o!.at) ?? 0 });
  }
  return out;
}

/** A lists document with these saved targets (everything else in it kept as it is; newest first, at most SAVED_TARGETS_MAX). */
export function withSavedTargets(lists: unknown, saved: readonly SavedTarget[]): Record<string, unknown> {
  const doc = { ...(obj(lists) ?? {}) };
  const entry = obj(doc[SAVED_TARGETS_LIST]) ?? {};
  doc[SAVED_TARGETS_LIST] = { ...entry, saved: saved.slice(0, SAVED_TARGETS_MAX).map((s) => ({ id: s.id, name: s.name, at: s.at })) };
  return doc;
}

/** Player ids typed or pasted: numbers, "[123]", profile or attack links — in order, no repeats, at most `max`. */
export function parseTargetIds(text: string, max = TARGET_MAX_COUNT): number[] {
  const out: number[] = [];
  const re = /(?:XID=|user2ID=|ID=|\[)?(\d{1,9})\]?/gi;
  for (const m of text.matchAll(re)) {
    const id = Number(m[1]);
    if (id > 0 && !out.includes(id)) out.push(id);
    if (out.length >= max) break;
  }
  return out;
}
